import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface StateData {
  new: number;
  in_progress: number;
  on_hold: number;
  resolved: number;
  closed: number;
  cancelled: number;
}

interface StateBarChartProps {
  data: StateData;
  onBarClick?: (state: string) => void;
}

const stateLabels: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const stateColors: Record<string, string> = {
  new: 'rgba(59, 130, 246, 0.8)',      // blue
  in_progress: 'rgba(245, 158, 11, 0.8)', // amber
  on_hold: 'rgba(156, 163, 175, 0.8)',   // gray
  resolved: 'rgba(16, 185, 129, 0.8)',    // green
  closed: 'rgba(107, 114, 128, 0.8)',     // dark gray
  cancelled: 'rgba(239, 68, 68, 0.8)',    // red
};

const StateBarChart: React.FC<StateBarChartProps> = ({ data, onBarClick }) => {
  const stateKeys = Object.keys(stateLabels);
  const labels = stateKeys.map(key => stateLabels[key]);
  const values = stateKeys.map(key => data[key as keyof StateData] || 0);
  const colors = stateKeys.map(key => stateColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Tickets',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} tickets (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
        ticks: { precision: 0 }
      },
      y: {
        grid: { display: false }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(stateKeys[index]);
      }
    },
  };

  return (
    <div id="chart-state-bar" className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">State Distribution</h3>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {stateKeys.map((state, index) => (
          <button
            key={state}
            onClick={() => onBarClick?.(state)}
            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ backgroundColor: stateColors[state], color: 'white' }}
          >
            {stateLabels[state]}: {values[index]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default StateBarChart;
